import { Injectable, signal } from '@angular/core';

export interface WorkflowStageSummary {
  id: string;
  name: string;
  order: number;
  colorHex: string;
}

export interface WorkflowSummary {
  id: string;
  projectId: string;
  name: string;
  iconKey: string;
  colorHex: string;
  description?: string;
  defaultDueInDays: number | null;
  isAccessRestricted: boolean;
  stages: WorkflowStageSummary[];
}

export interface ProjectSummary {
  id: string;
  name: string;
  description?: string;
  coverImageUrl?: string | null;
}

export interface CardSummary {
  id: string;
  projectId: string;
  workflowId: string;
  workflowStageId: string;
  title: string;
  ownerName: string;
  dueDate: string | null;
  commentsCount: number;
  attachmentsCount: number;
  checklistDone: number;
  checklistTotal: number;
}

const WORKFLOW_STAGES: WorkflowStageSummary[] = [
  { id: 'backlog', name: 'Backlog', order: 1, colorHex: '#94A3B8' },
  { id: 'in-progress', name: 'Em andamento', order: 2, colorHex: '#3B82F6' },
  { id: 'done', name: 'Concluído', order: 3, colorHex: '#22C55E' },
];

const PROJECTS: ProjectSummary[] = [
  {
    id: 'aluraix',
    name: 'Premmio',
    description: 'Operação de produto e entregas semanais.',
    coverImageUrl: 'assets/images/logoPremmio.png',
  },
  {
    id: 'premmio',
    name: 'Plannio',
    description: 'Fluxo operacional de suporte e roadmap.',
    coverImageUrl: null,
  },
];

const WORKFLOWS: WorkflowSummary[] = [
  {
    id: 'features',
    projectId: 'aluraix',
    name: 'Product Features',
    iconKey: 'sparkles',
    colorHex: '#0EA5E9',
    description: 'Planejamento e entrega de novas funcionalidades.',
    defaultDueInDays: null,
    isAccessRestricted: false,
    stages: WORKFLOW_STAGES,
  },
  {
    id: 'bugs',
    projectId: 'aluraix',
    name: 'Bug Triage',
    iconKey: 'bug',
    colorHex: '#F97316',
    description: 'Correções e priorização técnica.',
    defaultDueInDays: 2,
    isAccessRestricted: true,
    stages: WORKFLOW_STAGES,
  },
  {
    id: 'roadmap',
    projectId: 'premmio',
    name: 'Roadmap Q2',
    iconKey: 'route',
    colorHex: '#8B5CF6',
    description: 'Planejamento trimestral do projeto.',
    defaultDueInDays: 5,
    isAccessRestricted: false,
    stages: WORKFLOW_STAGES,
  },
];

const CARDS: CardSummary[] = [
  {
    id: 'card-1',
    projectId: 'aluraix',
    workflowId: 'features',
    workflowStageId: 'backlog',
    title: 'Nova visão de projetos',
    ownerName: 'Ricardo',
    dueDate: '2026-03-18',
    commentsCount: 3,
    attachmentsCount: 2,
    checklistDone: 2,
    checklistTotal: 5,
  },
  {
    id: 'card-2',
    projectId: 'aluraix',
    workflowId: 'features',
    workflowStageId: 'in-progress',
    title: 'Refino de critérios de aceite',
    ownerName: 'Maria',
    dueDate: '2026-03-14',
    commentsCount: 4,
    attachmentsCount: 1,
    checklistDone: 4,
    checklistTotal: 6,
  },
  {
    id: 'card-3',
    projectId: 'aluraix',
    workflowId: 'bugs',
    workflowStageId: 'in-progress',
    title: 'Corrigir regressão de navegação',
    ownerName: 'João',
    dueDate: '2026-03-11',
    commentsCount: 6,
    attachmentsCount: 0,
    checklistDone: 1,
    checklistTotal: 3,
  },
  {
    id: 'card-4',
    projectId: 'premmio',
    workflowId: 'roadmap',
    workflowStageId: 'done',
    title: 'Mapear épicos de integração',
    ownerName: 'Ana',
    dueDate: null,
    commentsCount: 1,
    attachmentsCount: 3,
    checklistDone: 3,
    checklistTotal: 3,
  },
  {
    id: 'card-5',
    projectId: 'premmio',
    workflowId: 'roadmap',
    workflowStageId: 'backlog',
    title: 'Definir milestones de abril',
    ownerName: 'Ricardo',
    dueDate: '2026-03-22',
    commentsCount: 0,
    attachmentsCount: 0,
    checklistDone: 0,
    checklistTotal: 4,
  },
];

@Injectable({ providedIn: 'root' })
export class OperationsCatalogService {
  private readonly projectsSignal = signal<ProjectSummary[]>(PROJECTS);
  private readonly workflowsSignal = signal<WorkflowSummary[]>(WORKFLOWS);
  private readonly cardsSignal = signal<CardSummary[]>(CARDS);

  readonly projects = this.projectsSignal.asReadonly();
  readonly workflows = this.workflowsSignal.asReadonly();
  readonly cards = this.cardsSignal.asReadonly();

  getProjectById(projectId: string | null): ProjectSummary | null {
    if (!projectId) return null;
    return this.projects().find((project) => project.id === projectId) ?? null;
  }

  getWorkflowById(workflowId: string | null): WorkflowSummary | null {
    if (!workflowId) return null;
    return this.workflows().find((workflow) => workflow.id === workflowId) ?? null;
  }

  getWorkflowsByProject(projectId: string | null): WorkflowSummary[] {
    if (!projectId) return [];
    return this.workflows().filter((workflow) => workflow.projectId === projectId);
  }

  getCardsByWorkflow(workflowId: string | null): CardSummary[] {
    if (!workflowId) return [];
    return this.cards().filter((card) => card.workflowId === workflowId);
  }

  getCardsByProject(projectId: string | null): CardSummary[] {
    if (!projectId) return [];
    return this.cards().filter((card) => card.projectId === projectId);
  }
}
